import api, { USE_REAL_API } from './api';
import { jwtDecode } from 'jwt-decode';

// Use same localStorage keys as API axios instance expects
export const ACCESS_TOKEN_KEY = 'safonova_access_token';
export const REFRESH_TOKEN_KEY = 'safonova_refresh_token';
export const USER_DATA_KEY = 'safonova_user';

// Add mock tokens for development
export const MOCK_ACCESS_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6ItCY0LLQsNC9INCY0LLQsNC90L7QsiIsImVtYWlsIjoidGVzdEBleGFtcGxlLmNvbSIsImlhdCI6MTUxNjIzOTAyMiwiZXhwIjoxOTkzODQ5MDIyfQ.4xbBLFL2yT-BwnbOuQrNN0na9g9o0kKidNY_qcy_Vb8';
export const MOCK_REFRESH_TOKEN = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwiaWF0IjoxNTE2MjM5MDIyLCJleHAiOjIxMTYyMzkwMjJ9.nrMO7EbagcHNcUNTr9a9-Wj0ZsVfJAHEHcGhFl033Rk';

// Add MOCK_USER constant that was missing
export const MOCK_USER: User = {
  id: 1,
  name: 'Иван Иванов',
  email: 'test@example.com',
  role: 'user',
  phone: '+7 (999) 123-45-67',
  address: 'г. Москва, ул. Арбат, 10',
  avatar: 'https://img.heroui.chat/image/avatar?w=200&h=200&u=user1',
  createdAt: '2023-01-15T00:00:00.000Z',
  updatedAt: '2023-06-20T00:00:00.000Z'
};

// Define the User interface if it doesn't exist
export interface User {
  id: number;
  name: string;
  email: string;
  role?: string;
  phone?: string;
  address?: string;
  avatar?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface LoginCredentials {
  username: string;
  password: string;
  rememberMe?: boolean;
}

export interface RegisterData {
  name: string;
  username: string;
  password: string;
  passwordConfirmation: string;
  phone?: string;
}

// Authentication helper functions
export const getCurrentUser = (): User | null => {
  try {
    const userData = localStorage.getItem(USER_DATA_KEY);
    return userData ? JSON.parse(userData) : null;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

export const saveTokens = (accessToken: string, refreshToken?: string): void => {
  try {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    if (refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    }
  } catch (err) {
    console.warn('Unable to save tokens to localStorage', err);
  }
};

export const saveUserData = (userData: User): void => {
  try {
    localStorage.setItem(USER_DATA_KEY, JSON.stringify(userData));
  } catch (err) {
    console.warn('Unable to save user data to localStorage', err);
  }
};

export const clearAuthData = (): void => {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    localStorage.removeItem(USER_DATA_KEY);
  } catch (err) {
    console.warn('Unable to clear auth data from localStorage', err);
  }
};

export const getToken = (): string | null => {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch (err) {
    console.warn('Unable to read access token from localStorage', err);
    return null;
  }
};

export const getRefreshToken = (): string | null => {
  try {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  } catch (err) {
    console.warn('Unable to read refresh token from localStorage', err);
    return null;
  }
};

export const hasRefreshToken = (): boolean => {
  return !!getRefreshToken();
};

export const isTokenValid = (token: string | null): boolean => {
  try {
    if (!token) return false;

    const decoded: any = jwtDecode(token as string);
    if (!decoded || !decoded.exp) return false;

    // Check if token is expired (exp in seconds)
    const currentTime = Date.now() / 1000;
    return decoded.exp > currentTime;
  } catch (error) {
    console.error('Error validating token:', error);
    return false;
  }
};

// Update logout function to ensure it uses the defined constants
export const logout = (): void => {
  try {
    // Clear tokens and user data from local storage
    clearAuthData();
    
    // If using real API, call logout endpoint
    if (USE_REAL_API) {
      // You might want to call an API endpoint to invalidate the token
      // api.post('/auth/logout');
    }
  } catch (error) {
    console.error('Error during logout:', error);
  }
};

// Обновление access token с использованием refresh token
export const refreshAccessToken = async (): Promise<string | null> => {
  // Dev mode: return mock
  if (!USE_REAL_API) {
    console.log('[DEV] Мок-обновление токена');
    try {
      localStorage.setItem(ACCESS_TOKEN_KEY, MOCK_ACCESS_TOKEN);
    } catch (err) {
      // ignore
    }
    return MOCK_ACCESS_TOKEN;
  }

  try {
    const refreshToken = getRefreshToken();
    if (!refreshToken) return null;

    // backend expects { refresh: '<refresh_token>' }
    const response = await api.post('/users/token/refresh/', { refresh: refreshToken }, { headers: { 'Content-Type': 'application/json' } });
    const { access } = response.data;
    if (access) {
      saveTokens(access);
      return access;
    }
    return null;
  } catch (error) {
    console.error('Ошибка при обновлении токена:', error);
    clearAuthData();
    return null;
  }
};

// Авторизация пользователя
export const login = async (credentials: LoginCredentials): Promise<User> => {
  // Dev: use mock
  if (!USE_REAL_API) {
    console.log('[DEV] Мок-авторизация:', credentials);
    if (credentials.username === 'testuser' && credentials.password === 'password') {
      saveTokens(MOCK_ACCESS_TOKEN, MOCK_REFRESH_TOKEN);
      // fill mock user with username-based email for dev
      const devUser = { ...MOCK_USER, email: `${credentials.username}@example.com` };
      saveUserData(devUser);
      return devUser;
    } else {
      throw new Error('Неверный логин или пароль');
    }
  }

  // Production: exchange credentials for tokens
  try {
    const response = await api.post('/users/token/', { username: credentials.username, password: credentials.password }, { headers: { 'Content-Type': 'application/json' } });
    const { access, refresh } = response.data;

    if (!access) throw new Error('Access token not returned by server');

    saveTokens(access, refresh);

    // Fetch profile
    const profile = await getUserProfile();
    saveUserData(profile);
    return profile;
  } catch (error) {
    console.error('Ошибка авторизации:', error);
    throw error;
  }
};

// Регистрация пользователя
export const register = async (data: RegisterData): Promise<User> => {
  // 🔵 РАЗРАБОТКА: используем мок-данные
  if (!USE_REAL_API) {
    console.log('[DEV] Мок-регистрация:', data);
    // В моке просто возвращаем успешную регистрацию
    const mockUser = {
      ...MOCK_USER,
      name: data.name,
      email: data.username ? `${data.username}@example.com` : MOCK_USER.email,
      phone: data.phone
    };
    saveTokens(MOCK_ACCESS_TOKEN, MOCK_REFRESH_TOKEN); // Fix: remove third argument
    saveUserData(mockUser); // Add this line to properly save user data
    return mockUser;
  }
  
  // 🟢 ПРОДАКШН: раскомментируйте эту секцию для использования реального API
  /*
  try {
    const response = await api.post<AuthResponse>('/api/auth/register', data);
    const { accessToken, refreshToken, user } = response.data;
    saveTokens(accessToken, refreshToken, user);
    return user;
  } catch (error) {
    console.error('Ошибка при регистрации:', error);
    throw error;
  }
  */
  
  throw new Error('API регистрация не настроена');
};

// Получение профиля пользователя
export const getUserProfile = async (): Promise<User> => {
  // Dev: return stored user
  if (!USE_REAL_API) {
    console.log('[DEV] Получение мок-профиля пользователя');
    const user = getCurrentUser();
    if (!user) throw new Error('Пользователь не авторизован');
    return user;
  }

  try {
    const response = await api.get<User>('/users/profile/');
    const user = response.data;
    saveUserData(user);
    return user;
  } catch (error) {
    console.error('Ошибка при получении профиля:', error);
    throw error;
  }
};